import { html, render } from "lit-html"

import { repeat } from "lit-html/directives/repeat"
import { virtual, useState } from "haunted"

const TIME_RANGE = [6, 22]

const getHourDisplay = (hour) =>
  `${hour <= 12 ? hour : hour - 12}:00 ${hour < 12 ? "am" : "pm"}`

export const TimeRangeInput = virtual(({ name, id, start, end }) => {
  const [expanded, setExpanded] = useState(false)
  const [timeStart, setTimeStart] = useState(start)
  const [timeEnd, setTimeEnd] = useState(end)
  const [currentTimeStart, setCurrentTimeStart] = useState(start)
  const [currentTimeEnd, setCurrentTimeEnd] = useState(end)

  const hours = Array.from(
    { length: TIME_RANGE[1] - TIME_RANGE[0] + 1 },
    (v, k) => k + TIME_RANGE[0]
  )

  const inputValue = timeStart && timeEnd ? `${timeStart}:00,${timeEnd}:00` : ``

  const updateStart = (value) => {
    setTimeStart(value)
    setTimeEnd(Math.max(value, timeEnd))
  }

  const updateEnd = (value) => {
    setTimeEnd(value)
    setTimeStart(Math.min(value, timeStart))
  }

  const onUpdateInputValue = () => {
    const $input = document.getElementById(id)
    $input.dispatchEvent(new Event("change"))
  }

  const onApply = () => {
    setExpanded(false)
    setCurrentTimeStart(timeStart)
    setCurrentTimeEnd(timeEnd)
    onUpdateInputValue()
  }

  const onClear = () => {
    setExpanded(false)
    setTimeStart(null)
    setTimeEnd(null)
    setCurrentTimeStart(null)
    setCurrentTimeEnd(null)
    onUpdateInputValue()
  }

  const TimeRangeOptions = (currentValue, onSelect) => {
    return html`
      <div class="w-1/2">
        ${repeat(
          hours,
          (hour) => hour,
          (hour) =>
            html`<div
              class="px-6 py-4 cursor-pointer ${currentValue === hour
                ? `bg-blue-100 text-blue`
                : ``} hover:text-blue"
              @click=${() => {
                onSelect(hour)
              }}
            >
              ${getHourDisplay(hour)}
            </div>`
        )}
      </div>
    `
  }

  // TODO: Replace with aria setup? https://www.w3.org/TR/wai-aria-practices-1.2/examples/slider/slider-2.html
  // Also fix that values are read as integers, not times
  // Two range inputs with only the handles/thumbs allowing pointer events
  return html`
    <button
      class="button w-full rounded-md border-px border-gray-border min-h-40 bg-white px-4 py-0 justify-start text-black focus:outline-none hover:border-blue active:shadow-input-blue focus:shadow-input-blue"
      type="button"
      aria-label="Time range"
      aria-controls="time-range-dialog"
      aria-expanded=${expanded.toString()}
      id="time-range-button"
      ?data-has-value=${inputValue !== ``}
      @click=${() => setExpanded(!expanded)}
      @clear=${onClear}
    >
      Time:
      ${currentTimeStart && currentTimeEnd
        ? html`${getHourDisplay(currentTimeStart)} -
          ${getHourDisplay(currentTimeEnd)}`
        : "All time"}
      <i class="far fa-clock input-icon"></i>
    </button>
    <input id=${id} name=${name} type="hidden" .value=${inputValue} />
    <div
      id="time-range-dialog"
      class="time-range-container absolute top-full left-0 right-0 pb-1 z-1 overflow-hidden ${expanded
        ? ``
        : `hidden`}"
      aria-labelledby="time-range-button"
      role="dialog"
    >
      <div
        class="absolute top-0 left-0 bottom-0 right-0 h-full w-full rounded-md border-px border-gray-border bg-white"
        aria-hidden="true"
      >
        <div
          class="flex border-b-px border-gray-border text-sm text-gray-interactive font-bold"
        >
          <div class="w-1/2 pl-6 py-2">From</div>
          <div class="w-1/2 pl-5half py-2">To</div>
        </div>
        <div
          class="flex overflow-y-auto max-h-[328px] text-black
          [&::-webkit-scrollbar]:w-2
          [&::-webkit-scrollbar-thumb]:rounded-md
          [&::-webkit-scrollbar-thumb]:bg-gray"
        >
          ${TimeRangeOptions(timeStart, updateStart)}
          ${TimeRangeOptions(timeEnd, updateEnd)}
        </div>
        <div
          class="border-t-px border-gray-border px-6 py-4half flex justify-start"
        >
          <button
            type="button"
            class="button primary grow-0 h-9 mr-5"
            @click=${onApply}
          >
            Apply
          </button>
          <button
            type="button"
            class="button tetiary grow-0 h-9"
            @click=${onClear}
          >
            Clear
          </button>
        </div>
      </div>
    </div>
  `
})

export default function renderTimeRange(el) {
  const input = el.querySelector(`input[type='hidden']`)
  const inputValue = input.value
  const [start, end] = inputValue.trim()
    ? inputValue.split(",").map((val) => +val.split(":")[0])
    : [null, null]
  render(TimeRangeInput({ el, name: input.name, id: input.id, start, end }), el)
}
